import type { FormInstance } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Form, Input } from 'antd';
import { useEffect, useState } from 'react';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type {
    ProdutoBody,
    ProdutoBodyTabelaComissaoExcecao,
} from '@/features/produto/types/ProdutoBody';
import type { TabelaComissao } from '@/features/tabela-comissao/types/TabelaComissao';
import type { TabelaComissaoProduto } from '@/features/tabela-comissao/types/TabelaComissaoProduto';
import type { FormAction } from '@/types/FormAction';
import { TableDefault } from '@/components/antd/TableDefault';
import { Container } from '@/components/Container';
import { apiTabelaComissaoConsultarQueryOptions } from '@/features/tabela-comissao/api/apiTabelaComissaoConsultar';
import {
    apiTabelaComissaoProdutoGetQueryOptions,
    type TabelaComissaoProdutoGetParams,
} from '@/features/tabela-comissao/api/apiTabelaComissaoProdutoGet';
import { useDialog } from '@/std/dialog/useDialog';
import { columns, formatData, type RowComissaoProduto } from './produtoTabComissaoData';
import { ProdutoTabComissaoDrawer } from './ProdutoTabComissaoDrawer';

export type TDataTable =
    | (TabelaComissaoProduto & { hasPrecoDif: string })
    | (TabelaComissao & { hasPrecoDif?: string });

export function ProdutoTabComissao({
    form,
    action,
    setFormDirty,
}: {
    form: FormInstance<FormProdutoValues>;
    action?: FormAction;
    setFormDirty: (dirty: boolean) => void;
}) {
    const dialog = useDialog<RowComissaoProduto>();

    const [dataTable, setDataTable] = useState<TDataTable[]>();
    const [dataTableIndex, setDataTableIndex] = useState<number | undefined>();

    const pro_idpk = Form.useWatch('pro_idpk', form) ?? 0;

    const { data: dataComissao, isFetching: isFetchingComissao } = useQuery({
        ...apiTabelaComissaoConsultarQueryOptions(),
        enabled: action === 'cadastrar',
    });

    const params: TabelaComissaoProdutoGetParams = {
        produto_idpk: pro_idpk,
    };

    const { data: dataComissaoProduto, isFetching: isFetchingComissaoProduto } = useQuery({
        ...apiTabelaComissaoProdutoGetQueryOptions(params),
        enabled: action !== 'cadastrar' && !!pro_idpk,
    });

    const getOriginalValue = (idpk: number): number => {
        if (action === 'cadastrar') {
            const findOriginalValueConsultar = dataComissao?.registros.find(
                (gdc) => gdc?.tac_idpk === idpk,
            );

            return findOriginalValueConsultar?.tac_percentual || 0;
        }

        const findOriginalValue = dataComissaoProduto?.registros.find(
            (gdp) => gdp?.tac_idpk === idpk,
        );

        return findOriginalValue?.tac_percentual || 0;
    };

    const handleEditar = (row: RowComissaoProduto, index: number): void => {
        const produtosFormValues = form.getFieldsValue(true) as ProdutoBody;
        const tabelaComissao = produtosFormValues.tabela_comissao;

        const findIndex = (tabelaComissao || []).findIndex((tp) => tp.tac_idpk === row?.tac_idpk);

        setDataTableIndex(index);

        const newRow = { ...row };

        if (action === 'cadastrar') {
            newRow.tabela_comissao_excecao =
                tabelaComissao && findIndex >= 0
                    ? tabelaComissao[findIndex]?.tabela_comissao_excecao
                    : undefined;
        } else {
            newRow.tabela_comissao_excecao =
                tabelaComissao && findIndex >= 0
                    ? tabelaComissao[findIndex]?.tabela_comissao_excecao
                    : row?.tabela_comissao_excecao;
        }

        dialog.actions.edit(newRow);
    };

    const handleChangeExcecao = (flgAdd: boolean, values?: ProdutoBodyTabelaComissaoExcecao) => {
        if (dataTableIndex !== undefined && dataTableIndex >= 0) {
            if (dataTable?.[dataTableIndex]) {
                const currentItem = dataTable[dataTableIndex] as TDataTable;

                const updatedItem: TDataTable = {
                    ...currentItem,
                    hasPrecoDif: flgAdd ? 'Comissão diferenciada' : '',
                    tac_percentual: flgAdd
                        ? values?.tce_percentual || 0
                        : getOriginalValue(currentItem.tac_idpk),
                };

                const newDataTable = [...dataTable];
                newDataTable[dataTableIndex] = updatedItem;
                setDataTable(newDataTable);
            }
        }

        setFormDirty(true);
    };

    const handleCloseEdit = () => {
        setDataTableIndex(undefined);
    };

    useEffect(() => {
        if (action === 'cadastrar') {
            const newDataTable = structuredClone(dataComissao?.registros);
            setDataTable(newDataTable);
        }
    }, [dataComissao, action]);

    useEffect(() => {
        if (action !== 'cadastrar') {
            const newDataTable = structuredClone(dataComissaoProduto?.registros);
            const newData = (newDataTable || []).map((gp) => ({
                ...gp,
                hasPrecoDif: gp?.tabela_comissao_excecao?.tce_idpk ? 'Comissão diferenciada' : '',
                tac_percentual: gp?.tabela_comissao_excecao?.tce_percentual || gp?.tac_percentual,
            }));

            setDataTable(newData);
        }
    }, [dataComissaoProduto, action]);

    return (
        <Container>
            <Form.Item hidden={true} name='tabela_comissao' noStyle={true}>
                <Input />
            </Form.Item>

            <TableDefault
                columns={columns({ handleEditar })}
                dataSource={formatData(dataTable || [])}
                loading={isFetchingComissaoProduto || isFetchingComissao}
                rowKey='tac_idpk'
            />

            <ProdutoTabComissaoDrawer
                dialog={dialog}
                form={form}
                handleChangeExcecao={handleChangeExcecao}
                handleCloseEdit={handleCloseEdit}
            />
        </Container>
    );
}
